import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BcdApplicationStatus } from 'app/core/enums/generated/BcdApplicationStatus';
import { InsulinQueueTab } from 'app/insulin-program/intake-queue/insulin-intake-queue.service';
import { LinkComponent } from 'app/shared/link/link.component';
import { InsulinDashboardService } from 'app/insulin-program/shared/insulin-dashboard.service';
import { InsulinHeaderFacilityService } from 'app/insulin-program/shared/insulin-header-facility.service';
import { StatusSeverity, statusSeverity } from 'app/insulin-program/shared/insulin-status';
import { ResizableWidgetComponent } from '../resizable-widget/resizable-widget.component';

/** One summary card. The count is what the card is for, so it leads. */
interface SummaryCard {
    label: string;
    count: number;
    /** A line under the figure, where the card has something to add to its label. */
    sub?: string;
    severity: StatusSeverity;
    /** Where the card's own figure can be seen row by row, already narrowed to what it counted. */
    href: string;
}

/**
 * The three Insulin queue backlogs: waiting on a reviewer, on an appointment, and on an Rx.
 *
 * A separate widget from the two lists on purpose. The cards answer "how much is there" and the
 * lists answer "which ones", and only the lists carry applicant names - so somebody who wants the
 * numbers on their dashboard need not take five patients' names along with them.
 *
 * Reads the same response as the other two through the shared service, so a card and the list beside
 * it cannot show different snapshots of the same queue.
 *
 * Scoped to the facility selected in HOMER's header when it is one of the reader's Insulin
 * facilities, and to every facility they are associated with otherwise. The acceptance criteria
 * require each card to agree with the queue it opens, so every card link names that same facility
 * and the queue lists what the card counted.
 *
 * The cards reflow rather than switching on orientation: they are laid out with flex-wrap and a
 * minimum width, so a wide widget puts all three in a line, a narrower one wraps them, and a tall
 * narrow one stacks them. The dashboard sets width and height independently, so a resize can land on
 * any aspect ratio - reflowing handles every one of them, where an orientation test would only
 * handle the two it was written for.
 */
@Component({
    selector: 'pcg-insulin-queue-summary-widget',
    imports: [
        ResizableWidgetComponent
        , LinkComponent
    ],
    templateUrl: './insulin-queue-summary-widget.component.html',
    styleUrl: './insulin-queue-summary-widget.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsulinQueueSummaryWidgetComponent {

    // #region Injections
    private readonly dashboardService = inject(InsulinDashboardService);
    private readonly header = inject(InsulinHeaderFacilityService);
    private readonly destroyRef = inject(DestroyRef);
    // #endregion

    // #region State
    readonly isLoading = this.dashboardService.isLoading;
    readonly hasError = this.dashboardService.hasError;

    private readonly dashboard = this.dashboardService.dashboard;

    readonly isEnabled = computed(() => this.dashboard()?.isEnabled === true);
    readonly unavailableMessage = this.dashboardService.unavailableMessage;
    // #endregion

    /**
     * The three counts, straight from the server.
     *
     * Each card takes the colour of the status it counts, read from the same `statusSeverity` the
     * queue, the review screen and the status page colour their tags with - so a card and the tag
     * on the rows it is counting are never two different colours for the same thing. Naming the
     * status rather than the colour is what keeps that true: change a status's colour once and
     * every surface follows, this card included.
     *
     * The Requires Review card carries the oldest-waiting figure as its subtitle, which is the SLA
     * cue the acceptance criteria ask for. It lives here rather than on the list widget because a
     * count and its worst case belong on the same card, and the list already shows each row's own
     * age.
     */
    /**
     * The queue, opened on exactly the list this card counted: its tab and, where the card counted a
     * single status, that status.
     *
     * `clear=1` first, so the queue starts from no filters rather than whatever was left from the
     * last visit. The queue keeps its filters between visits, and without this a card followed after
     * the Awaiting Rx card kept that card's Rx filter - and the queue then moved to a tab that lists
     * it, so Requires Review opened on the awaiting list. Any kept facility, assignee or date filter
     * would have made the list shorter than the number clicked in the same way. The operational
     * report's links do the same. The facility the figures were read for goes with it, so the queue
     * narrows to the same one.
     */
    private queueUrl(tab: InsulinQueueTab, status?: BcdApplicationStatus): string {
        const named = status == null ? '' : `&status=${status}`;
        const facilityId = this.header.facilityId();
        const facility = facilityId == null ? '' : `&facilityId=${facilityId}`;
        return `insulin-program/intake-queue/list?clear=1&tab=${tab}${named}${facility}`;
    }

    readonly cards = computed<SummaryCard[]>(() => {
        const vm = this.dashboard();
        const oldest = vm?.oldestRequiresReviewDays ?? null;

        return [
            {
                label: 'Requires review'
                , count: vm?.requiresReview ?? 0
                , sub: oldest === null
                    ? 'Nothing waiting'
                    : `Oldest ${oldest} ${oldest === 1 ? 'day' : 'days'} in queue`
                , severity: statusSeverity(BcdApplicationStatus.RequiresReview)
                , href: this.queueUrl(InsulinQueueTab.RequiresReview)
            }
            , {
                label: 'Awaiting scheduling'
                // Every approval still waiting on an appointment, however long ago it was approved -
                // a backlog, like the Awaiting Rx card beside it, not a month's output (DEPCS-4967).
                // Links to the status itself, so the queue lists exactly what was counted.
                , count: vm?.awaitingScheduling ?? 0
                , severity: statusSeverity(BcdApplicationStatus.ApprovedAwaitingScheduling)
                , href: this.queueUrl(InsulinQueueTab.Approved, BcdApplicationStatus.ApprovedAwaitingScheduling)
            }
            , {
                label: 'Awaiting Rx association'
                , count: vm?.awaitingRxAssociation ?? 0
                // Names the subset that is stopped, because "12 awaiting" reads as twelve things
                // ticking along when three of them are waiting on somebody to act.
                , sub: (vm?.needsRxReview ?? 0) > 0
                    ? `${vm.needsRxReview} needs review`
                    : 'Within the 30-day QS1 window'
                , severity: statusSeverity(BcdApplicationStatus.AwaitingRxAssociation)
                // Straight to the status the card counted, rather than to a queue the reader then has
                // to narrow by hand. The status, not the queue's "Awaiting" Rx filter: that one leaves
                // out held applications, which this count includes and the sub-line calls out. The
                // held rows are flagged "Needs review" in the list's Rx column.
                , href: this.queueUrl(InsulinQueueTab.Approved, BcdApplicationStatus.AwaitingRxAssociation)
            }
        ];
    });

    // Asks again whenever the header facility changes. The figures follow it - that facility's
    // when it is one of the reader's Insulin facilities, all of theirs otherwise - which is the same
    // default the intake queue these link into starts from (InsulinHeaderFacilityService). Waits
    // until the header facility is known, so the first request is already the right one.
    private readonly followHeaderFacility = effect(() => {
        if (!this.header.ready()) { return; }
        this.header.facilityId();
        untracked(() => this.load());
    });

    private load(): void {
        this.dashboardService
            .refresh()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
